/**
 * Printing, replacing citaria-print.exe.
 *
 * The helper set a DEVMODE — paper size, orientation, 600 DPI, copy count — and
 * drew the sheet with GDI. None of that is reachable from a browser, so this
 * hands the composite to the browser's own print pipeline and the operator
 * confirms it in Chrome's dialog. What that costs, stated plainly because it is
 * what an operator will notice on the floor:
 *
 *  - A dialog per guest. There is no silent print without launching Chrome
 *    behind a flag, and this build deliberately requires no install at all.
 *  - No printer enumeration, so the §6 improvement of naming an unusable
 *    printer's reason is gone; Chrome's own destination list is what there is.
 *  - No 600 DPI request. The browser rasterises at the driver's resolution, and
 *    a 4R composite is comfortably above it, so this is a lost guarantee rather
 *    than a lost capability.
 *  - No `--dry-run`. `calibrationSheet` replaces it: one sheet that prints the
 *    whole useful inset range at once, so the bleed is found by looking instead
 *    of by burning six sheets.
 *
 * Copies are PAGES, not a dialog field. Asking the operator to type a number
 * into Chrome for every second print is exactly the sort of step that goes
 * wrong in front of a customer, so N copies is one job of N identical pages and
 * the dialog's own copy count stays at 1.
 *
 * The operator still has to set "Margins: None" and "Scale: 100%" once — Chrome
 * remembers both per destination — and `PrintStep` says so on screen. Without
 * them the browser rescales the sheet and the inset stops meaning inches.
 */

import { canvasContext, makeCanvas, toBlob } from '@/core/canvas'
import { calibrationInsets, inches, printPageGeometry, type Inset } from '@/core/print'
import type { ImageOrientation } from '@/core/types'

export interface PrintRequest {
  /** The composite to print, at the background's native pixel size. */
  image: Blob
  /** Orientation of the composite, before `invertOrientation` is applied. */
  orientation: ImageOrientation
  invertOrientation: boolean
  /** Signed inset in inches, in Portrait terms. Negative bleeds off the sheet. */
  inset: Inset
  copies: number
}

/**
 * Sends one sheet to the browser's print dialog.
 *
 * Resolves when the dialog has been dismissed — printed or cancelled; the
 * browser does not say which, so the caller cannot and must not report "printed"
 * as a confirmed fact.
 */
export async function printSheet(request: PrintRequest): Promise<void> {
  const geometry = printPageGeometry(
    request.orientation,
    request.invertOrientation,
    request.inset,
  )

  const url = URL.createObjectURL(request.image)
  try {
    const pages = Array.from(
      { length: Math.max(1, Math.trunc(request.copies)) },
      () => `<div class="sheet"><img src="${url}" alt=""></div>`,
    ).join('')

    await printDocument(
      `<style>
        @page { size: ${inches(geometry.pageWidthIn)} ${inches(geometry.pageHeightIn)}; margin: 0 }
        html, body { margin: 0; padding: 0; background: #fff }
        /* Each sheet is its own page box; the last must not push a blank one. */
        .sheet {
          position: relative;
          width: ${inches(geometry.pageWidthIn)};
          height: ${inches(geometry.pageHeightIn)};
          overflow: hidden;
          break-after: page;
        }
        .sheet:last-child { break-after: auto }
        .sheet img {
          position: absolute;
          left: ${inches(geometry.leftIn)};
          top: ${inches(geometry.topIn)};
          width: ${inches(geometry.widthIn)};
          height: ${inches(geometry.heightIn)};
        }
      </style>${pages}`,
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Prints one sheet showing the same photo at every inset in the useful range.
 *
 * This is what replaces `citaria-print.exe --dry-run`. The operator prints it
 * once, looks at which panel reaches the paper edge without losing anything,
 * and types that number into Settings. Each panel is labelled with its own
 * value so the answer can be read off the sheet rather than counted.
 */
export async function calibrationSheet(image: Blob): Promise<void> {
  const url = URL.createObjectURL(image)
  try {
    const insets = calibrationInsets()
    const geometry = printPageGeometry('Portrait', false, { x: 0, y: 0 })

    // Six panels down one 4R sheet: enough to judge the edge, small enough that
    // the whole range fits on a single piece of paper.
    const panelHeight = geometry.pageHeightIn / insets.length

    const panels = insets
      .map((inset) => {
        const width = geometry.pageWidthIn - inset * 2
        const height = panelHeight - inset * 2
        return `<div class="panel">
            <img src="${url}" alt="" style="left:${inches(inset)};top:${inches(inset)};width:${inches(width)};height:${inches(height)}">
            <span class="tag">${inset.toFixed(2)}"</span>
          </div>`
      })
      .join('')

    await printDocument(
      `<style>
        @page { size: ${inches(geometry.pageWidthIn)} ${inches(geometry.pageHeightIn)}; margin: 0 }
        html, body { margin: 0; padding: 0; background: #fff }
        .panel {
          position: relative;
          width: ${inches(geometry.pageWidthIn)};
          height: ${inches(panelHeight)};
          overflow: hidden;
          outline: 0.5pt solid #000;
        }
        .panel img { position: absolute }
        .tag {
          position: absolute; left: 2pt; bottom: 2pt;
          font: bold 7pt/1 monospace; color: #000;
          background: #fff; padding: 1pt 2pt;
        }
      </style>${panels}`,
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Renders `body` in a hidden iframe and prints it.
 *
 * An iframe rather than a new window: a popup would be blocked on a machine
 * where the operator has not allowed them, and printing the console's own
 * document would need every screen to carry print styles. The iframe carries
 * exactly the sheet and nothing else.
 */
async function printDocument(body: string): Promise<void> {
  const frame = document.createElement('iframe')
  // Off-screen rather than `display: none`: a hidden iframe does not lay out,
  // and a document that has never laid out prints blank in Chrome.
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0'
  document.body.appendChild(frame)

  try {
    const doc = frame.contentDocument
    const view = frame.contentWindow
    if (!doc || !view) throw new Error('Tidak bisa menyiapkan halaman cetak')

    doc.open()
    doc.write(`<!doctype html><html><head><meta charset="utf-8"></head><body>${body}</body></html>`)
    doc.close()

    // Every image must be decoded before print() or the sheet comes out blank.
    // This is the single most common way browser printing fails, and it fails
    // silently — on paper.
    await Promise.all(
      [...doc.images].map((image) =>
        image.complete ? image.decode().catch(() => undefined) : imageLoaded(image),
      ),
    )

    await new Promise<void>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        resolve()
      }

      // Chrome fires afterprint when the preview is dismissed, printed or not.
      view.addEventListener('afterprint', finish, { once: true })
      // Some builds never fire it for an iframe. The fallback only controls
      // when the iframe is torn down, so being late costs nothing.
      setTimeout(finish, 60_000)

      view.focus()
      view.print()
    })
  } finally {
    frame.remove()
  }
}

function imageLoaded(image: HTMLImageElement): Promise<void> {
  return new Promise((resolve) => {
    image.addEventListener('load', () => resolve(), { once: true })
    // A broken image must not hang the print behind a promise that never
    // settles; a missing panel is visible, a hung console is not.
    image.addEventListener('error', () => resolve(), { once: true })
  })
}

/**
 * The test pattern the calibration sheet prints.
 *
 * Nested bands rather than a photo: what the operator needs to see is how much
 * of the image the printer actually puts on the paper, and a band that has been
 * cut off is obvious where a cropped photograph is not. The outermost band is
 * the edge of the sheet — if it survives, the inset is bleeding correctly.
 */
export async function calibrationImage(): Promise<Blob> {
  // Aspect does not matter — each panel stretches this to its own box — so this
  // is sized for crisp bands rather than for 4R.
  const width = 800
  const height = 200
  const canvas = makeCanvas(width, height)
  const ctx = canvasContext(canvas)

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)

  // Five bands from the edge inwards, alternating so a partial cut is readable.
  const bands = ['#007a75', '#ffffff', '#007a75', '#ffffff', '#007a75']
  const step = Math.floor(height / (bands.length * 2))

  bands.forEach((colour, index) => {
    const inset = index * step
    ctx.strokeStyle = colour
    ctx.lineWidth = step
    ctx.strokeRect(inset + step / 2, inset + step / 2, width - inset * 2 - step, height - inset * 2 - step)
  })

  return toBlob(canvas, 'image/png')
}
